<script setup lang="ts">
import { computed } from 'vue'
import sortBy from 'lodash/sortBy'
import reverse from 'lodash/reverse'
import { formatMoneyFromPence } from '@/helpers/moneyDisplay'
import { useAddDonationModal } from '@/composables/useAddDonationModal'
import { useManualGivingDonations } from '@/composables/useManualGivingDonations'
import {
  donationsForTaxYearPeriod,
  isDonorAddedDonation,
  sumDonationAmounts,
  sumGiftAidAmounts,
} from '@/helpers/taxPackDonations'
import DonationListRow from '@/components/dashboard/ui/DonationListRow.vue'
import RecentDonationsEmptyPanel from '@/components/dashboard/home/RecentDonationsEmptyPanel.vue'
import RecentDonationsGivingRecordFooter from '@/components/dashboard/home/RecentDonationsGivingRecordFooter.vue'
import type { DonorFinancialYear } from '@/types/donorFinancialYear'

const HOME_RECENT_DONATIONS_LIMIT = 3

const props = defineProps<{
  currentYear: DonorFinancialYear
  sortedYears: DonorFinancialYear[]
}>()

const { openAddDonationModal } = useAddDonationModal()

const { manualDonations } = useManualGivingDonations()

const manualForCurrentYear = computed(() =>
  donationsForTaxYearPeriod(
    manualDonations.value.filter(isDonorAddedDonation),
    props.currentYear.period
  )
)

const totalDonated = computed(
  () =>
    props.currentYear.totalDonations +
    sumDonationAmounts(manualForCurrentYear.value)
)

const totalGiftAid = computed(
  () =>
    props.currentYear.totalGiftAid +
    sumGiftAidAmounts(manualForCurrentYear.value)
)

const donationCount = computed(
  () => props.currentYear.donations.length + manualForCurrentYear.value.length
)

const recentDonations = computed(() =>
  reverse(
    sortBy(
      [
        ...props.sortedYears.flatMap((year) => year.donations),
        ...manualDonations.value.filter(isDonorAddedDonation),
      ],
      'time'
    )
  ).slice(0, HOME_RECENT_DONATIONS_LIMIT)
)

const onAddDonation = () => {
  openAddDonationModal()
}
</script>

<template>
  <div class="sa-page-stack">
    <section aria-labelledby="paid-impact-heading">
      <div class="sa-section-hd">
        <h2 id="paid-impact-heading" class="sa-section-title">
          {{ $t('Dashboard.Home.ImpactHeadingCurrentTaxYear') }}
        </h2>
      </div>
      <div class="sa-metrics">
        <div class="sa-metric-card sa-metric-card--blue">
          <div class="sa-metric-label">
            {{ $t('Dashboard.Home.MetricDonated') }}
          </div>
          <div class="sa-metric-value">
            {{ formatMoneyFromPence(totalDonated) }}
          </div>
          <div class="sa-metric-sub">
            {{ $t('Dashboard.Home.MetricThisTaxYear') }}
          </div>
        </div>
        <div class="sa-metric-card sa-metric-card--teal">
          <div class="sa-metric-label">
            {{ $t('Dashboard.Home.MetricGiftAid') }}
          </div>
          <div class="sa-metric-value">
            {{ formatMoneyFromPence(totalGiftAid) }}
          </div>
          <div class="sa-metric-sub">
            {{ $t('Dashboard.Home.MetricGiftAidSub') }}
          </div>
        </div>
        <div class="sa-metric-card sa-metric-card--amber">
          <div class="sa-metric-label">
            {{ $t('Dashboard.Home.MetricCount') }}
          </div>
          <div class="sa-metric-value">{{ donationCount }}</div>
          <div class="sa-metric-sub">
            {{ $t('Dashboard.Home.MetricThisTaxYear') }}
          </div>
        </div>
      </div>
    </section>

    <section aria-labelledby="paid-recent-donations-heading">
      <div class="sa-section-hd sa-section-hd--with-action">
        <h2 id="paid-recent-donations-heading" class="sa-section-title">
          {{ $t('Dashboard.Home.RecentDonationsHeading') }}
        </h2>
        <button
          type="button"
          class="sa-btn-primary sa-btn-primary--compact sa-section-hd__action"
          @click="onAddDonation"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path
              fill-rule="evenodd"
              d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
              clip-rule="evenodd"
            />
          </svg>
          {{ $t('Dashboard.Home.AddDonationCta') }}
        </button>
      </div>
      <div v-if="recentDonations.length" class="sa-donation-list">
        <DonationListRow
          v-for="donation in recentDonations"
          :key="donation.id"
          :donation="donation"
        />
        <RecentDonationsGivingRecordFooter />
      </div>
      <div
        v-else
        class="sa-donation-list"
        role="group"
        aria-labelledby="paid-recent-donations-heading"
      >
        <RecentDonationsEmptyPanel />
        <RecentDonationsGivingRecordFooter />
      </div>
    </section>
  </div>
</template>
